import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Button,
  MenuItem,
  TextField,
  LinearProgress,
  Tooltip,
  Alert,
} from "@mui/material";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import { DRONE_STRATEGIES } from "../simulation/droneStrategies";
import { runExperiments } from "../simulation/experimentRunner";
import {
  STATE_WATER,
  STATE_BURNING,
  STATE_SMOLDERING,
  STATE_EMPTY,
  STATE_INFRASTRUCTURE,
  STATE_INFRASTRUCTURE_BURNT,
  STATE_RETARDANT,
} from "../simulation/constants";

// Burn-footprint thumbnail rendered from a final engine state
const MiniMap = ({ finalState, size = 108 }) => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !finalState) return;
    const rows = finalState.length;
    const cols = finalState[0].length;
    canvas.width = cols;
    canvas.height = rows;
    const ctx = canvas.getContext("2d");
    const img = ctx.createImageData(cols, rows);
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const s = finalState[r][c];
        let rgb;
        if (s === STATE_WATER) rgb = [35, 60, 120];
        else if (s === STATE_BURNING || s === STATE_SMOLDERING) rgb = [255, 70, 30];
        else if (s === STATE_EMPTY) rgb = [60, 42, 34];
        else if (s === STATE_INFRASTRUCTURE_BURNT) rgb = [150, 30, 30];
        else if (s === STATE_INFRASTRUCTURE) rgb = [140, 145, 150];
        else if (s === STATE_RETARDANT) rgb = [230, 110, 170];
        else rgb = [30, 80, 40];
        const i = (r * cols + c) * 4;
        img.data[i] = rgb[0];
        img.data[i + 1] = rgb[1];
        img.data[i + 2] = rgb[2];
        img.data[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  }, [finalState]);
  return (
    <canvas
      ref={ref}
      style={{
        width: size,
        height: size,
        imageRendering: "pixelated",
        borderRadius: 6,
        border: "1px solid rgba(255,255,255,0.12)",
        display: "block",
      }}
    />
  );
};

const ResultRow = ({ result, rank, worstLoss, onApply, isBaseline }) => {
  const barPct = worstLoss > 0 ? (result.infraLost / worstLoss) * 100 : 0;
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
      <MiniMap finalState={result.finalState} size={86} />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={0.8} alignItems="center">
          {rank === 0 && !isBaseline && (
            <EmojiEventsRoundedIcon sx={{ fontSize: 16, color: "#ffd54f" }} />
          )}
          <Typography variant="subtitle2" noWrap>
            {result.strategyName}
          </Typography>
          {result.strategyId !== "none" && (
            <Typography variant="caption" color="text.secondary">
              · density {result.density} ({result.stationCount} stations)
            </Typography>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block">
          {result.infraLost.toFixed(1)} infra cells lost
          {result.savedVsBaseline != null && result.strategyId !== "none" && (
            <>
              {" · "}
              <Box component="span" sx={{ color: result.savedVsBaseline > 0 ? "success.main" : "error.main", fontWeight: 700 }}>
                {result.savedVsBaseline >= 0 ? "saves" : "costs"}{" "}
                {Math.abs(result.savedVsBaseline).toFixed(1)}
              </Box>{" "}
              vs. no drones
            </>
          )}
          {result.strategyId !== "none" && ` · ${Math.round(result.drops)} drops`}
        </Typography>
        <Box
          sx={{
            mt: 0.5,
            height: 6,
            borderRadius: 3,
            backgroundColor: "rgba(255,255,255,0.06)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: `${barPct}%`,
              height: "100%",
              background:
                rank === 0 && !isBaseline
                  ? "linear-gradient(90deg,#66bb6a,#a5d6a7)"
                  : "linear-gradient(90deg,#ef5350,#ff8a65)",
            }}
          />
        </Box>
      </Box>
      {result.strategyId !== "none" && (
        <Button size="small" variant="outlined" onClick={() => onApply(result)}>
          Use
        </Button>
      )}
    </Stack>
  );
};

const StrategyLab = ({ scenario, getEngine, getSimTimeMs, onApplyStrategy, disabled }) => {
  const [selected, setSelected] = useState(["none", "nearest", "valueWeighted", "intercept"]);
  const [densities, setDensities] = useState([5]);
  const [horizon, setHorizon] = useState(48);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [experiment, setExperiment] = useState(null);
  const [error, setError] = useState(null);

  const toggle = (list, setList, v) =>
    setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const configCount =
    selected.filter((s) => s !== "none").length * densities.length +
    (selected.includes("none") ? 1 : 0);

  const run = async () => {
    const engine = getEngine();
    if (!engine || !scenario || running) return;
    setRunning(true);
    setError(null);
    setProgress(0);
    try {
      const configs = [];
      if (selected.includes("none")) configs.push({ strategyId: "none", density: 0 });
      for (const s of selected.filter((x) => x !== "none")) {
        for (const d of densities) configs.push({ strategyId: s, density: d });
      }
      const res = await runExperiments({
        engine,
        weatherTimeline: scenario.weather,
        startMs: getSimTimeMs(),
        horizonHours: horizon,
        replicates: 3,
        configs,
        onProgress: (p) => setProgress(p),
      });
      setExperiment(res);
    } catch (err) {
      console.error("Experiment failed:", err);
      setError(err.message || String(err));
    } finally {
      setRunning(false);
    }
  };

  const worstLoss = experiment
    ? Math.max(...experiment.results.map((r) => r.infraLost), 0.001)
    : 0;

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <ScienceRoundedIcon fontSize="small" color="secondary" />
        <Typography variant="subtitle2">What-if Strategy Lab</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Race drone deployment algorithms head-to-head from the current fire
        state. Every permutation fights the identical fire (paired random
        seeds, 3 replicates each) so differences are pure strategy.
      </Typography>

      <Typography variant="overline" color="text.secondary">
        Algorithms
      </Typography>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.6} sx={{ mb: 1 }}>
        {Object.entries(DRONE_STRATEGIES).map(([id, s]) => (
          <Tooltip key={id} title={s.description} placement="top">
            <Chip
              size="small"
              label={s.short}
              color={selected.includes(id) ? "secondary" : "default"}
              variant={selected.includes(id) ? "filled" : "outlined"}
              onClick={() => toggle(selected, setSelected, id)}
            />
          </Tooltip>
        ))}
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <Box>
          <Typography variant="overline" color="text.secondary" display="block">
            Tower density
          </Typography>
          <Stack direction="row" spacing={0.6}>
            {[3, 5, 8].map((d) => (
              <Chip
                key={d}
                size="small"
                label={`${d}`}
                color={densities.includes(d) ? "secondary" : "default"}
                variant={densities.includes(d) ? "filled" : "outlined"}
                onClick={() => toggle(densities, setDensities, d)}
              />
            ))}
          </Stack>
        </Box>
        <TextField
          select
          size="small"
          label="Horizon"
          value={horizon}
          onChange={(e) => setHorizon(Number(e.target.value))}
          sx={{ width: 90 }}
        >
          <MenuItem value={24}>24 h</MenuItem>
          <MenuItem value={48}>48 h</MenuItem>
          <MenuItem value={72}>72 h</MenuItem>
        </TextField>
        <Button
          variant="contained"
          color="secondary"
          onClick={run}
          disabled={disabled || running || configCount === 0 || densities.length === 0}
        >
          {running ? "Racing…" : `Run ${configCount} configs`}
        </Button>
      </Stack>

      {running && (
        <Box sx={{ mb: 1 }}>
          <LinearProgress variant="determinate" value={progress * 100} color="secondary" />
          <Typography variant="caption" color="text.secondary">
            {Math.round(progress * 100)}% · {configCount} configs × 3 replicates
          </Typography>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {experiment && !running && (
        <Box>
          <Typography variant="overline" color="text.secondary">
            Ranked outcomes · next {experiment.horizonHours}h
          </Typography>
          {experiment.results.map((r, i) => (
            <ResultRow
              key={`${r.strategyId}-${r.density}`}
              result={r}
              rank={i}
              worstLoss={worstLoss}
              isBaseline={r.strategyId === "none"}
              onApply={(res) =>
                onApplyStrategy({ strategyId: res.strategyId, density: res.density })
              }
            />
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Thumbnails show each strategy's projected burn footprint. "Use"
            applies that algorithm + density to the live simulation.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default StrategyLab;
